var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/interface/index.ts
import { z as z8 } from "zod";

// src/interface/claim.ts
import { z as z2 } from "zod";

// src/interface/internal.ts
import { z } from "zod";
var YesOrNoSchema = z.enum(["Yes", "No"]);
var VersionSchema = z.literal("6.0.0");
var DataSetSchema = z.literal("01");

// src/interface/claim.ts
var ISODateTimeSchema = z2.string();
var TransactionSchema = z2.object({
  DataSet: DataSetSchema,
  /**
   * Any name here, doesn't matter
   */
  PMAVersion: z2.string(),
  /**
   * Any name here, doesn't matter
   */
  PMAName: z2.string(),
  /**
   * Combination of Scheme/Administrator/Plan
   *
   * @example 90080
   */
  RoutingCode: z2.string(),
  /**
   * Quro Reference Number for an Invoice
   *
   * Can be auto generated when we send the claim - guid is too long.
   * Must be populated and unique for every claim
   */
  InvoiceNum: z2.string(),
  SavedDateTime: ISODateTimeSchema,
  /**
   * Any version, we use the API so we can just use that
   *
   * @example API
   */
  MedpraxVersion: z2.string().optional()
});
var PostalAddressSchema = z2.object({
  Address: z2.string(),
  Suburb: z2.string(),
  Town: z2.string(),
  Code: z2.string()
});
var PhysicalAddressSchema = z2.object({
  Address: z2.string(),
  Suburb: z2.string(),
  Town: z2.string(),
  Code: z2.string()
});
var DemographicsSchema = z2.object({
  WkTelephone: z2.string(),
  HMTelephone: z2.string(),
  Fax: z2.string(),
  Cellphone: z2.string(),
  Email: z2.string(),
  PostalAddress: PostalAddressSchema,
  PhysicalAddress: PhysicalAddressSchema
});
var ISODateSchema = z2.string();
var BaseProviderSchema = z2.object({
  PracticeName: z2.string(),
  /**
   * Practice number of the billing entity 7 or 13 digits
   */
  PracticeNum: z2.string(),
  /**
   * Specialty code can sometimes be derived from the practice number
   */
  SpecialtyCode: z2.string(),
  /**
   * Can just be left as `0`
   */
  SubSpecialtyCode: z2.literal("0")
});
var BillingProviderSchema = BaseProviderSchema.extend({
  "@Type": z2.literal("Billing")
});
var TreatingProviderSchema = BaseProviderSchema.extend({
  /**
   * 1 treating entity must be populated can be the same as the billing entity if the billing entity is the treating entity
   */
  "@Type": z2.literal("Treating"),
  DispensingDoctor: YesOrNoSchema,
  /**
   * HPCSA MP number in the format of `mp1234567`
   */
  HPCSA: z2.string()
});
var GenderSchema = z2.enum(["Male", "Female"]);
var MemberSchema = z2.object({
  DOB: ISODateSchema,
  Name: z2.string(),
  Surname: z2.string(),
  /**
   * Medical aid member number
   */
  MANum: z2.string(),
  SchemeName: z2.string(),
  Initials: z2.string().optional(),
  ID: z2.string().optional(),
  Gender: GenderSchema.optional(),
  PlanName: z2.string().optional(),
  OptionName: z2.string().optional(),
  /**
   * Maybe scheme code - TBC
   */
  SchemeReg: z2.string().optional(),
  Demographics: DemographicsSchema.optional()
});
var PatientSchema = z2.object({
  DOB: z2.string(),
  Name: z2.string(),
  Surname: z2.string(),
  Initials: z2.string(),
  /**
   * Medical aid dependant code
   */
  DepCode: z2.string(),
  /**
   * Financial identifier for customer (if billing patient), for us we can just use the patient id
   */
  AccNum: z2.string(),
  /**
   * Is this a newborn baby - always No for us
   */
  NewBornInd: YesOrNoSchema,
  /**
   * Not sure what this is
   */
  Identifier: z2.string().optional(),
  Gender: GenderSchema.optional(),
  Demographics: DemographicsSchema.optional()
});
var DiagnosisCodeSchema = z2.object({
  Set: z2.literal("ICD"),
  Version: z2.literal("10"),
  Value: z2.string(),
  Description: z2.string().optional(),
  /**
   * Primary for the first code, secondary for all subsequent codes
   */
  Type: z2.enum(["Primary", "Secondary"])
});
var COIDFieldsSchema = z2.object({
  /**
   * This will be `No` for us
   */
  COIDInd: YesOrNoSchema,
  RecInd: z2.string().optional(),
  DOI: z2.string().optional(),
  EmployerName: z2.string().optional(),
  EmployeeNo: z2.string().optional(),
  EmployerRegNo: z2.string().optional()
});
var MoneyCentsSchema = z2.string();
var ClaimFinancialsSchema = z2.object({
  TotalBilledAmt: MoneyCentsSchema,
  TotalDiscount: MoneyCentsSchema,
  NettBilledAmt: MoneyCentsSchema
});
var ChargeCodeSchema = z2.object({
  "@Type": z2.literal("Procedure"),
  Set: z2.literal("Tariff"),
  Version: z2.literal("1").optional(),
  /**
   * Charge item code
   */
  Value: z2.string(),
  /**
   * Charge item title/description
   */
  Description: z2.string()
});
var LineItemUnitsSchema = z2.literal("Unit");
var PlaceServiceCode = /* @__PURE__ */ ((PlaceServiceCode2) => {
  PlaceServiceCode2["HomeCare"] = "12";
  return PlaceServiceCode2;
})(PlaceServiceCode || {});
var BalancedBillingSchema = z2.object({
  /**
   * Medical liable
   */
  MALiable: MoneyCentsSchema,
  /**
   * Patient liable
   */
  PatLiable: MoneyCentsSchema
});
var LineItemFinancialsSchema = z2.object({
  BilledAmt: MoneyCentsSchema,
  GrossAmt: MoneyCentsSchema,
  Discount: MoneyCentsSchema.optional(),
  Levy: MoneyCentsSchema.optional(),
  ContainerFee: MoneyCentsSchema.optional(),
  DispenseFee: MoneyCentsSchema.optional(),
  ServiceFee: MoneyCentsSchema.optional(),
  ProfFee: MoneyCentsSchema.optional(),
  BalancedBilling: BalancedBillingSchema.optional()
});
var LineItemSchema = z2.object({
  /**
   * To confirm the types for this - e.g. consumables and procedures
   */
  "@Type": z2.literal("Procedure"),
  /**
   * Line number (1 - n)
   *
   * @example '1', '25'
   */
  UniqueID: z2.string(),
  ChargeCode: ChargeCodeSchema,
  DOS: ISODateSchema,
  /**
   * Check with TK
   */
  HospInd: YesOrNoSchema,
  /**
   * Place service code - depends on where the procedure took place
   */
  PlaceServiceCode: z2.nativeEnum(PlaceServiceCode),
  LineItemFinancials: LineItemFinancialsSchema,
  DiagnosisCodes: DiagnosisCodeSchema.array(),
  /**
   * TBC
   */
  Num: z2.string().optional(),
  /**
   * Always Private for us - required for dental or optometry claims
   */
  PricingInd: z2.enum(["Private", "Public"]),
  /**
   * Check with TK
   */
  ChargeStart: ISODateTimeSchema.optional(),
  /**
   * Check with TK
   */
  ChargeEnd: ISODateTimeSchema.optional(),
  /**
   * Number of charge item being billed for
   *
   * @example '1', '25'
   */
  ChargeQuantity: z2.string().optional(),
  /**
   * Unit to be used to measure the amount of item
   */
  ChargeUnit: LineItemUnitsSchema.optional()
});
var DetailsSchema = COIDFieldsSchema.extend({
  DiagnosisCodes: DiagnosisCodeSchema.array(),
  /**
   * Total amounts for the claim
   */
  ClaimFinancials: ClaimFinancialsSchema,
  LineItems: LineItemSchema.array(),
  /**
   * Always pay the provider
   */
  PayWho: z2.literal("Provider"),
  /**
   * Check with TK
   */
  HospInd: YesOrNoSchema,
  /**
   * TBC what this si
   */
  Num: z2.string().optional(),
  /**
   * Practice number of doctor that referred - don't have this
   */
  ReferralNum: z2.string().optional(),
  /**
   * Admission Date - Check with TK
   */
  AdmDateTime: ISODateSchema.optional(),
  /**
   * Discharge date - Check with TK
   */
  DisDateTime: ISODateSchema.optional(),
  /**
   * Optometry scripts - we don't prescribe these
   */
  OptomScripts: z2.tuple([]).optional()
});
var ProvidersSchema = z2.tuple([BillingProviderSchema, TreatingProviderSchema]);
var CreateClaimRequestSchema = z2.object({
  ProviderMessage: z2.object({
    "@xmlns:hb": z2.literal("healthbridge.co.za/messaging"),
    // Leave as is, no need to change
    "@Version": VersionSchema,
    // Leave as is, no need to change
    Claim: z2.object({
      Transaction: TransactionSchema,
      /**
       * These wll likely be hardcoded as some specific Quro details. The treating provider can be the same as the billing entity if they are the same
       */
      Providers: ProvidersSchema,
      /**
       * Details of the medical aid main member
       */
      Member: MemberSchema,
      Patient: PatientSchema,
      Details: DetailsSchema
    })
  })
});
var CreateClaimSuccessResponseSchema = z2.object({
  MessageId: z2.string(),
  Message: z2.string(),
  InvoiceNum: z2.string()
});
var CreateClaimErrorSchema = z2.object({
  objectName: z2.string(),
  field: z2.string(),
  rejectedValue: z2.string(),
  codes: z2.string().array(),
  defaultMessage: z2.string(),
  code: z2.string()
});
var CreateClaimErrorResponseSchema = z2.object({
  Message: z2.string(),
  Errors: CreateClaimErrorSchema.array()
});
var CreateClaimResponseSchema = z2.union([
  CreateClaimSuccessResponseSchema,
  CreateClaimErrorResponseSchema
]);

// src/interface/claim-reversal.ts
import { z as z3 } from "zod";
var CreateClaimReversalRequestSchema = z3.object({
  ProviderMessage: z3.object({
    Reversal: z3.object({
      Transaction: TransactionSchema
    })
  })
});
var CreateClaimReversalErrorSchema = z3.object({
  objectName: z3.string(),
  field: z3.string(),
  rejectedValue: z3.string(),
  codes: z3.string().array(),
  defaultMessage: z3.string(),
  code: z3.string()
});
var CreateClaimReversalErrorResponseSchema = z3.object({
  Message: z3.string(),
  Errors: CreateClaimReversalErrorSchema.array()
});
var CreateClaimReversalSuccessResponseSchema = z3.object({
  MessageId: z3.string(),
  Message: z3.string(),
  InvoiceNum: z3.string()
});
var CreateClaimReversalResponseSchema = z3.union([
  CreateClaimReversalSuccessResponseSchema,
  CreateClaimReversalErrorResponseSchema
]);

// src/interface/claim-response.ts
import { z as z4 } from "zod";
var IntegerStrSchema = z4.custom();
var MoneyCentsSchema2 = IntegerStrSchema;
var ClaimResponseClaimFinancialsSchema = z4.object({
  /**
   * Total amount claimed for
   */
  TotalClaimedAmt: MoneyCentsSchema2,
  /**
   * Total amount paid by medical aid
   */
  TotalPaidProvAmt: MoneyCentsSchema2,
  /**
   * Outstanding amount
   */
  BalanceOutstanding: MoneyCentsSchema2
});
var StatusCode = {
  AwaitingResponse: "2",
  ReceivedByHealthbridge: "3",
  ValidatedByHealthbridge: "4",
  ValidatedByFunder: "6",
  Processed: "7",
  Rejected: "8",
  AwaitingReversalResponse: "10",
  ReversalAccepted: "11",
  ReversalProcessed: "12",
  ReversalRejected: "13",
  PartRejected: "17",
  PartProcessedByMedicalAid: "21",
  Null: "99"
};
var StatusCodeSchema = z4.nativeEnum(StatusCode);
var StatusDescSchema = z4.string();
var ClaimResponseLineItemFinancialsSchema = z4.object({
  /**
   * What was the claimed amount
   */
  BilledAmt: MoneyCentsSchema2,
  /**
   * What the medical aid will pay
   */
  MAPayable: MoneyCentsSchema2,
  /**
   * Scheme rate for the item claimed
   */
  MedicalAidRate: MoneyCentsSchema2.optional()
});
var ResponseSchema = z4.object({
  /**
   * Reason - Explanation of the payment being handled. This can indicate a valid claim that doesn't get paid
   * Error - Something wrong with claim
   */
  "@Type": z4.union([z4.literal("Reason"), z4.literal("Error")]),
  /**
   * Code comes directly from the medical aid
   */
  Code: IntegerStrSchema,
  /**
   * Directly from the medical aid
   */
  Desc: z4.string()
});
var ClaimResponseLineItemSchema = z4.object({
  UniqueID: IntegerStrSchema,
  StatusCode: StatusCodeSchema,
  StatusDesc: StatusDescSchema,
  MALineRefNum: z4.string().optional(),
  Editable: YesOrNoSchema,
  Reversible: YesOrNoSchema,
  PayWho: z4.literal("Provider"),
  LineItemFinancials: ClaimResponseLineItemFinancialsSchema,
  Responses: ResponseSchema.array()
});
var ClaimResponseDetailsSchema = z4.object({
  ClaimFinancials: ClaimResponseClaimFinancialsSchema,
  LineItems: ClaimResponseLineItemSchema.array(),
  Responses: ResponseSchema.array().optional()
});
var ClaimResponseTransactionSchema = z4.object({
  ReferenceNum: z4.string(),
  DataSet: DataSetSchema,
  InvoiceNum: z4.string()
});
var ClaimSchema = z4.object({
  Transaction: ClaimResponseTransactionSchema,
  Details: ClaimResponseDetailsSchema,
  Disclaimer: z4.string().optional()
});
var ClaimResponseSchema = z4.object({
  MedicalAidMessage: z4.object({
    "@Version": VersionSchema,
    Claim: ClaimSchema
  })
});

// src/interface/example.ts
var example_exports = {};
__export(example_exports, {
  claimResponseProcessedPartiallyPaid: () => claimResponseProcessedPartiallyPaid,
  claimResponseRejected: () => claimResponseRejected,
  claimResponseValidatedNotPaid: () => claimResponseValidatedNotPaid
});

// src/interface/response-processed-partially-paid.example.ts
var claimResponseProcessedPartiallyPaid = {
  MedicalAidMessage: {
    "@Version": "6.0.0",
    Claim: {
      Transaction: {
        ReferenceNum: "220812180944152uvnesro",
        DataSet: "01",
        InvoiceNum: "QM00313642208-00002"
      },
      Details: {
        ClaimFinancials: {
          TotalClaimedAmt: "50712",
          TotalPaidProvAmt: "42570",
          BalanceOutstanding: "8142"
        },
        LineItems: [
          {
            UniqueID: "3",
            StatusCode: "7",
            StatusDesc: "Processed",
            MALineRefNum: "8465880422",
            Editable: "No",
            Reversible: "Yes",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "6187",
              MAPayable: "0",
              MedicalAidRate: "6187"
            },
            Responses: [
              {
                "@Type": "Reason",
                Code: "514",
                Desc: "Benefits limit reached/depleted/exhausted. (514)"
              }
            ]
          },
          {
            UniqueID: "1",
            StatusCode: "7",
            StatusDesc: "Processed",
            MALineRefNum: "8465880422",
            Editable: "No",
            Reversible: "Yes",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "42570",
              MAPayable: "42570",
              MedicalAidRate: "42570"
            },
            Responses: [
              {
                "@Type": "Reason",
                Code: "247",
                Desc: "Enhanced Fee Included in Bulk Payment Above. (247)"
              }
            ]
          },
          {
            UniqueID: "2",
            StatusCode: "7",
            StatusDesc: "Processed",
            MALineRefNum: "8465880422",
            Editable: "No",
            Reversible: "Yes",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "1955",
              MAPayable: "0",
              MedicalAidRate: "1955"
            },
            Responses: [
              {
                "@Type": "Reason",
                Code: "514",
                Desc: "Benefits limit reached/depleted/exhausted. (514)"
              }
            ]
          }
        ]
      },
      Disclaimer: "Medscheme-Nexus has confirmed receipt of your claim. All line items in this claim have been fully processed, including processing against the member's benefits. Payment is not guaranteed as a result of this submission."
    }
  }
};

// src/interface/response-rejected.example.ts
var claimResponseRejected = {
  MedicalAidMessage: {
    "@Version": "6.0.0",
    Claim: {
      Transaction: {
        ReferenceNum: "220818194119697o21aijl",
        DataSet: "01",
        InvoiceNum: "QM202208-00006"
      },
      Details: {
        ClaimFinancials: {
          TotalClaimedAmt: "58396",
          TotalPaidProvAmt: "0",
          BalanceOutstanding: "58396"
        },
        LineItems: [
          {
            UniqueID: "0",
            StatusCode: "8",
            StatusDesc: "Rejected",
            Editable: "Yes",
            Reversible: "No",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "480",
              MAPayable: "0"
            },
            Responses: [
              {
                "@Type": "Error",
                Code: "757",
                Desc: "1. 757 Invalid treating provider.."
              }
            ]
          },
          {
            UniqueID: "1",
            StatusCode: "8",
            StatusDesc: "Rejected",
            Editable: "Yes",
            Reversible: "No",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "43410",
              MAPayable: "0"
            },
            Responses: [
              {
                "@Type": "Error",
                Code: "757",
                Desc: "1. 757 Invalid treating provider.."
              }
            ]
          },
          {
            UniqueID: "2",
            StatusCode: "8",
            StatusDesc: "Rejected",
            Editable: "Yes",
            Reversible: "No",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "4697",
              MAPayable: "0"
            },
            Responses: [
              {
                "@Type": "Error",
                Code: "757",
                Desc: "1. 757 Invalid treating provider.."
              }
            ]
          },
          {
            UniqueID: "3",
            StatusCode: "8",
            StatusDesc: "Rejected",
            Editable: "Yes",
            Reversible: "No",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "9809",
              MAPayable: "0"
            },
            Responses: [
              {
                "@Type": "Error",
                Code: "757",
                Desc: "1. 757 Invalid treating provider.."
              }
            ]
          }
        ],
        Responses: [
          {
            "@Type": "Error",
            Code: "9808",
            Desc: "Invalid billing provider,Content Failure.,"
          }
        ]
      }
    }
  }
};

// src/interface/response-validated-not-paid.example.ts
var claimResponseValidatedNotPaid = {
  MedicalAidMessage: {
    "@Version": "6.0.0",
    Claim: {
      Transaction: {
        ReferenceNum: "22083014341085837bgcuk",
        DataSet: "01",
        InvoiceNum: "MD05875592208-00037"
      },
      Details: {
        ClaimFinancials: {
          TotalClaimedAmt: "45590",
          TotalPaidProvAmt: "41740",
          BalanceOutstanding: "3850"
        },
        LineItems: [
          {
            UniqueID: "0",
            StatusCode: "6",
            StatusDesc: "Validated by Medical Aid",
            MALineRefNum: "B2IBFFNBTAL3",
            Editable: "No",
            Reversible: "No",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "1630",
              MAPayable: "1630"
            },
            Responses: [
              {
                "@Type": "Error",
                Code: "0",
                Desc: "No Error"
              }
            ]
          },
          {
            UniqueID: "1",
            StatusCode: "6",
            StatusDesc: "Validated by Medical Aid",
            MALineRefNum: "B2IBFFNBTAL3",
            Editable: "No",
            Reversible: "No",
            PayWho: "Provider",
            LineItemFinancials: {
              BilledAmt: "43960",
              MAPayable: "40110"
            },
            Responses: [
              {
                "@Type": "Error",
                Code: "0",
                Desc: "No Error"
              }
            ]
          }
        ],
        Responses: [
          {
            "@Type": "Reason",
            Code: "0",
            Desc: "Accepted"
          }
        ]
      }
    }
  }
};

// src/interface/person.ts
import {
  HumanNamePrefix,
  IdentifierCode as ResourceIdentifierCode,
  CountryCode
} from "@quromedical/fhir-common";
import { z as z5 } from "zod";
var IdentifierCode = ((IdentifierCode2) => {
  IdentifierCode2[IdentifierCode2["saIdNumber"] = ResourceIdentifierCode.southAfricanIdNumber] = "saIdNumber";
  IdentifierCode2[IdentifierCode2["namibiaIdNumber"] = ResourceIdentifierCode.namibianIdNumber] = "namibiaIdNumber";
  IdentifierCode2[IdentifierCode2["passportNumber"] = ResourceIdentifierCode.passportNumber] = "passportNumber";
  return IdentifierCode2;
})(IdentifierCode || {});
var GenderSchema2 = z5.union([
  z5.literal("unknown"),
  z5.literal("male"),
  z5.literal("female"),
  z5.literal("other")
]);
var CoordinatesSchema = z5.object({
  latitude: z5.number(),
  longitude: z5.number()
});
var GeneralSchema = z5.object({
  prefixName: z5.nativeEnum(HumanNamePrefix),
  givenName: z5.string(),
  familyName: z5.string(),
  gender: GenderSchema2,
  birthDate: z5.string(),
  identifierCode: z5.nativeEnum(IdentifierCode),
  identifierValue: z5.string()
});
var GeneralUpdateSchema = z5.object({
  id: z5.string().uuid(),
  general: GeneralSchema
});
var ContactSchema = z5.object({
  /**
   * In formik you can reference this as `phoneNumbers.0` and `phoneNumbers.1` to populate array
   * values if there isn't an appropriate multi-text input
   */
  telecomPhoneNumbers: z5.array(z5.string()),
  telecomEmail: z5.string(),
  /**
   * Google address place_Id is from google place autocomplete
   */
  addressPlaceId: z5.string().optional(),
  addressCoordinates: CoordinatesSchema.optional(),
  addressLines: z5.array(z5.string()),
  addressCity: z5.string(),
  addressCountry: z5.nativeEnum(CountryCode),
  addressPostalCode: z5.string()
});

// src/interface/medical-aid.ts
import { z as z6 } from "zod";
var SchemeSchema = z6.object({
  administrator: z6.string().optional(),
  schemeCode: z6.string(),
  schemeName: z6.string()
});
var MedicalAidSchema = SchemeSchema.extend({
  plan: z6.string(),
  membershipNumber: z6.string()
});
var MedicalAidMembershipSchema = z6.object({
  medicalAid: MedicalAidSchema,
  dependantCode: z6.string(),
  /**
   * Can this person be used for a next of kin of a patient
   */
  isEligibleAsNextOfKin: z6.boolean(),
  /**
   * Is this person the main member of a medical aid plan
   */
  isMainMember: z6.boolean(),
  isActive: z6.boolean(),
  start: z6.string(),
  end: z6.string().optional(),
  /**
   * This is derived directly from the Healthbridge API
   */
  status: z6.string()
});
var MedicalAidPersonSchema = z6.object({
  general: GeneralSchema.partial(),
  contact: ContactSchema.partial(),
  medicalAidMembership: MedicalAidMembershipSchema.partial().optional()
});
var MedicalAidValidationResponseSchema = z6.array(
  MedicalAidPersonSchema.partial().optional()
);
var MedicalAidPersonSchemaOmitContact = z6.object({
  general: GeneralSchema.partial(),
  medicalAidMembership: MedicalAidMembershipSchema.partial().optional()
});

// src/interface/ClaimClient.ts
import { set } from "@quromedical/utils";
import axios2 from "axios";

// src/Authentication/LoginClient.ts
import axios from "axios";
var LoginClient = class {
  constructor(baseUrl, credentials) {
    this.baseUrl = baseUrl;
    this.credentials = credentials;
    this.login = async (loginURL) => {
      const config = {
        headers: {
          contentType: "application/x-www-form-urlencoded"
        }
      };
      const oauth2 = this.credentials;
      const stringifiedCredentials = new URLSearchParams({
        client_id: oauth2.client_id,
        client_secret: oauth2.client_secret,
        scope: oauth2.scope,
        grant_type: oauth2.grant_type
      });
      try {
        const result = await this.client.post(loginURL, stringifiedCredentials, config);
        return result.data;
      } catch (error) {
        console.error(error);
        throw error;
      }
    };
    this.client = axios.create({
      baseURL: baseUrl
    });
  }
};

// src/Authentication/HealthbridgeLoginClient.ts
import isJwtTokenExpired from "jwt-check-expiry";
import { URL } from "url";
var HealthbridgeLoginClient = class extends LoginClient {
  constructor(baseUrl, credentials) {
    super(baseUrl, credentials);
    this.version = "1.0";
    this.loginPath = `/authorization/${this.version}/token`;
    this.getToken = async () => {
      const isExpired = this.authToken ? isJwtTokenExpired(this.authToken) : true;
      if (!isExpired) {
        return this.authToken;
      }
      const loginResult = await this.login(this.loginURL);
      if (loginResult.error) {
        throw new Error(`Error logging in to Healthbridge: ${loginResult.error}`);
      }
      this.authToken = loginResult.access_token;
      return this.authToken;
    };
    this.loginURL = new URL(this.loginPath, baseUrl).href;
  }
};

// src/Authentication/types.ts
import { z as z7 } from "zod";
var HealthbridgeTokenSchema = z7.object({
  error: z7.string().optional(),
  access_token: z7.string(),
  token_type: z7.string(),
  expires_in: z7.number(),
  ext_expires_in: z7.number()
});

// src/interface/ClaimClient.ts
var ClaimClient = class _ClaimClient {
  constructor({ baseUrl, credentials, logger }) {
    this.apiVersion = "1.0";
    this.basePath = `/claim-service/${this.apiVersion}/`;
    /**
     * Will submit a claim to the healthbridge API. Will respond with the error detail if the API call
     * is not successful
     */
    this.createClaim = async (claim) => {
      try {
        const result = await this.client.post(
          `${this.basePath}claim`,
          claim
        );
        this.logger.info(`Successfully created claim`);
        return result.data;
      } catch (err) {
        const axiosError = err;
        if (!axiosError.response?.data) {
          const errors = axiosError.response?.data.Errors;
          return {
            Message: "Healthbridge API did not send back a response",
            Errors: errors || []
          };
        }
        return axiosError.response.data;
      }
    };
    /**
     * Will reverse a claim to the healthbridge API. Will respond with the error detail if the API call
     * is not successful
     */
    this.reverseClaim = async (claimReversal) => {
      try {
        const result = await this.client.post(
          `${this.basePath}reversal`,
          claimReversal
        );
        this.logger.info(`Successfully reversed claim`);
        return result.data;
      } catch (err) {
        const axiosError = err;
        if (!axiosError.response?.data) {
          const errors = axiosError.response?.data.Errors;
          return {
            Message: "Healthbridge API did not send back a response",
            Errors: errors || []
          };
        }
        return axiosError.response.data;
      }
    };
    this.client = _ClaimClient.createAuthenticatedClient(baseUrl, credentials);
    this.logger = logger;
  }
  static {
    this.createAuthenticatedClient = (baseURL, credentials) => {
      const client = axios2.create({
        baseURL
      });
      client.interceptors.request.use(async (request) => {
        const loginClient = new HealthbridgeLoginClient(baseURL, credentials);
        const authToken = await loginClient.getToken();
        set(request, "headers.Authorization", `Bearer ${authToken}`);
        return request;
      });
      return client;
    };
  }
};

// src/interface/FamilyCheckClient.ts
import { set as set2 } from "@quromedical/utils";
import axios3 from "axios";

// src/constant/index.ts
var BILLING_PRACTICE_NUMBER = "0904287";
var TREATING_PRACTICE_NUMBER = "0904287";
var PLATFORM_NAME = "Insight";

// src/interface/FamilyCheckClient.ts
var FamilyCheckClient = class _FamilyCheckClient {
  constructor({ baseUrl, credentials, searchClient }) {
    this.apiVersion = "1.0";
    this.familyCheckPath = `/family-check/api/${this.apiVersion}/FamilyCheck/`;
    this.queryFamilyCheck = async (query) => {
      const params = {
        ...query,
        pmaName: PLATFORM_NAME,
        treatingPracticeNumber: TREATING_PRACTICE_NUMBER,
        billingPracticeNumber: BILLING_PRACTICE_NUMBER
      };
      const path = `${this.familyCheckPath}Query`;
      const result = await this.client.post(path, void 0, {
        params
      });
      return result.data;
    };
    this.getSchemes = async () => {
      const result = await this.client.get(
        `${this.familyCheckPath}Administrators`
      );
      return result.data;
    };
    this.renewSchemes = async () => {
      const schemes = await this.getSchemes();
      const cacheData = schemes.filter((d) => d.schemeCode).map((scheme) => ({ ...scheme, objectID: scheme.schemeCode }));
      await this.searchClient.add(cacheData);
    };
    this.searchSchemes = async (query, count) => this.searchClient.search({ query, limit: count });
    this.searchClient = searchClient;
    this.client = _FamilyCheckClient.createAuthenticatedClient(baseUrl, credentials);
  }
  static {
    this.createAuthenticatedClient = (baseURL, credentials) => {
      const client = axios3.create({
        baseURL
      });
      client.interceptors.request.use(async (request) => {
        const loginClient = new HealthbridgeLoginClient(baseURL, credentials);
        const authToken = await loginClient.getToken();
        set2(request, "headers.Authorization", `Bearer ${authToken}`);
        return request;
      });
      return client;
    };
  }
};

// src/interface/index.ts
var AdministratorSchema = z8.object({
  schemeName: z8.string(),
  schemeCode: z8.string(),
  canSearchOnIDNumber: z8.boolean()
});
var FamilyCheckMembershipNumberRequestSchema = z8.object({
  schemeCode: z8.string(),
  membershipNumber: z8.string()
});
var FamilyCheckIdNumberRequestSchema = z8.object({
  schemeCode: z8.string(),
  idNumber: z8.string()
});
var FamilyCheckRequestSchema = z8.union([
  FamilyCheckIdNumberRequestSchema,
  FamilyCheckMembershipNumberRequestSchema
]);
var AllowedDependencyType = ["Adult dependant", "Child", "Principle party", "Spouse"];
var DependencyTypeSchema = z8.enum(AllowedDependencyType);
var ResponseDetailsSchema = z8.object({
  disclaimer: z8.string(),
  responseDateTime: z8.date(),
  enquiryNumber: z8.string(),
  referenceNumber: z8.string()
});
var PatientDetailSchema = z8.object({
  membershipNumber: z8.string(),
  schemeName: z8.string(),
  plan: z8.string(),
  dependantCode: z8.string(),
  firstName: z8.string(),
  lastName: z8.string(),
  /**
   * Can be any of these:
   *
   * The membership is valid and active
   * The membership is not valid
   * The membership is suspended
   *
   */
  status: z8.string(),
  dateOfBirth: z8.string(),
  gender: z8.string(),
  idNumber: z8.string(),
  dependencyType: DependencyTypeSchema,
  /**
   * Can be any of these in any possible casing:
   *
   * ```
   * 'mr' | 'mrs' | 'miss' | 'dr' | 'mnr' | 'mev' | 'mej'
   * ```
   */
  title: z8.string(),
  initials: z8.string(),
  membershipEffectiveDateFrom: z8.string(),
  membershipToDate: z8.string(),
  benefitStartDate: z8.string(),
  joinDate: z8.string(),
  leftDate: z8.string(),
  recordDate: z8.string()
});
var FamilyRecordSchema = z8.object({
  patientDetails: PatientDetailSchema.array()
});
var FamilyCheckResponseSchema = z8.object({
  responseDetails: ResponseDetailsSchema,
  familyRecord: FamilyRecordSchema
});
var AdministratorsResponseSchema = AdministratorSchema.array();
var AdministratorsRequestSchema = z8.object({});
var SubmitClaimRequestSchema = CreateClaimRequestSchema;
var SubmitClaimReversalRequestSchema = CreateClaimReversalRequestSchema;
var ResultSuccessSchema = z8.object({
  type: z8.literal("success"),
  data: CreateClaimSuccessResponseSchema
});
var ResultErrorSchema = z8.object({
  type: z8.literal("error"),
  error: z8.string()
});
var SubmitClaimResultSchema = z8.discriminatedUnion("type", [
  ResultSuccessSchema,
  ResultErrorSchema
]);
var ReversalSuccessSchema = z8.object({
  type: z8.literal("success"),
  data: CreateClaimReversalSuccessResponseSchema
});
var SubmitClaimReversalResultSchema = z8.discriminatedUnion("type", [
  ReversalSuccessSchema,
  ResultErrorSchema
]);

// src/mapping/mappings.ts
import { createFhirDate, HumanNamePrefix as HumanNamePrefix2 } from "@quromedical/fhir-common";
import { isValid } from "date-fns";
var humanPrefixes = {
  mr: HumanNamePrefix2.mr,
  mrs: HumanNamePrefix2.mrs,
  miss: HumanNamePrefix2.miss,
  dr: HumanNamePrefix2.dr,
  mnr: HumanNamePrefix2.mr,
  mev: HumanNamePrefix2.mrs,
  mej: HumanNamePrefix2.mrs
};
var mapTitleToHumanNamePrefix = (title = "") => {
  const prefix = humanPrefixes[title.toLowerCase()];
  return prefix;
};
var genders = {
  male: "male",
  female: "female"
};
var mapGenderToPersonGender = (gender = "unknown") => {
  const prefix = genders[gender.toLowerCase()];
  return prefix || "unknown";
};
var isEligibleAsNextOfKin = (patient) => patient.dependencyType !== "Child";
var isMainMember = (patient) => patient.dependencyType === "Principle party";
var convertDate = (date) => {
  if (!date) {
    return void 0;
  }
  const parsedDate = new Date(date);
  const isDateValid = isValid(parsedDate);
  if (!isDateValid) {
    return void 0;
  }
  return createFhirDate(parsedDate);
};
var convertIdentifier = (id) => {
  if (!id) {
    return void 0;
  }
  if (id.toLowerCase().includes("no record")) {
    return void 0;
  }
  return id;
};
var isPatientActiveMedicalAidMember = (patient) => patient.status.toLocaleLowerCase() === "the membership is valid and active";
var mapHealthbridgePatientToPerson = (schemeCode, patient) => ({
  general: {
    birthDate: convertDate(patient.dateOfBirth),
    gender: mapGenderToPersonGender(patient.gender),
    identifierValue: convertIdentifier(patient.idNumber),
    identifierCode: IdentifierCode.saIdNumber,
    givenName: patient.firstName,
    familyName: patient.lastName,
    prefixName: mapTitleToHumanNamePrefix(patient.title)
  },
  medicalAidMembership: {
    medicalAid: {
      schemeCode,
      schemeName: patient.schemeName,
      plan: patient.plan,
      membershipNumber: patient.membershipNumber
    },
    dependantCode: patient.dependantCode,
    start: convertDate(patient.membershipEffectiveDateFrom),
    end: convertDate(patient.membershipToDate),
    status: patient.status,
    isActive: isPatientActiveMedicalAidMember(patient),
    isEligibleAsNextOfKin: isEligibleAsNextOfKin(patient),
    isMainMember: isMainMember(patient)
  }
});

// src/HealthBridgeClient.ts
import { partial, sortby } from "@quromedical/utils";
var HealthbridgeClient = class _HealthbridgeClient {
  constructor({ baseUrl, credentials, logger, searchClient }) {
    this.queryFamilyCheck = async (request) => {
      const result = await this.familyCheckClient.queryFamilyCheck(request);
      const patientDetails = result?.familyRecord?.patientDetails;
      const sortedList = sortby(patientDetails, ["dependantCode"]);
      return sortedList;
    };
    /**
     * Handles the family check and returns all members in the `models` data structure
     */
    this.queryMedicalAidMembership = async (request) => {
      const results = await this.queryFamilyCheck(request);
      const mapWithSchemeCode = partial(mapHealthbridgePatientToPerson, request.schemeCode);
      return (results || []).map(mapWithSchemeCode);
    };
    /**
     * Handles the family check and maps it to the `models` data structure
     */
    this.queryMedicalAidMembershipForDependant = async (request, dependantCode) => {
      const results = await this.queryFamilyCheck(request);
      const found = results?.find((result) => result.dependantCode === dependantCode);
      if (!found) {
        return void 0;
      }
      return mapHealthbridgePatientToPerson(request.schemeCode, found);
    };
    /**
     * This gets schemes (but the healthbridge api calls this endpoint administrators
     */
    this.getSchemes = async () => {
      const result = await this.familyCheckClient.searchSchemes("", 200);
      return result;
    };
    this.submitClaim = async (claim) => {
      const result = await this.claimClient.createClaim(claim);
      if (_HealthbridgeClient.isSuccessClaimResponse(result)) {
        return {
          type: "success",
          data: result
        };
      }
      const errorMessage = result?.Errors ? result?.Message || "A validation error ocurred when submitting claim" : "No data received from claim submission";
      return {
        type: "error",
        error: errorMessage
      };
    };
    this.submitClaimReversal = async (claimReversal) => {
      const result = await this.claimClient.reverseClaim(claimReversal);
      if (_HealthbridgeClient.isSuccessClaimReversalResponse(result)) {
        return {
          type: "success",
          data: result
        };
      }
      const errorMessage = result?.Errors ? result?.Message || "A validation error ocurred when submitting claim reversal" : "No data received from claim reversal submission";
      return {
        type: "error",
        error: errorMessage
      };
    };
    this.baseUrl = baseUrl;
    this.credentials = credentials;
    this.logger = logger;
    this.searchClient = searchClient;
    this.familyCheckClient = new FamilyCheckClient({
      baseUrl: this.baseUrl,
      credentials: this.credentials,
      searchClient: this.searchClient
    });
    this.claimClient = new ClaimClient({
      baseUrl: this.baseUrl,
      credentials: this.credentials,
      logger: this.logger
    });
  }
  static {
    this.isSuccessClaimReversalResponse = (response) => !!response.InvoiceNum;
  }
  static {
    this.isSuccessClaimResponse = (response) => !!response.InvoiceNum;
  }
};
export {
  AdministratorSchema,
  BalancedBillingSchema,
  BaseProviderSchema,
  BillingProviderSchema,
  COIDFieldsSchema,
  ChargeCodeSchema,
  ClaimClient,
  ClaimFinancialsSchema,
  ClaimResponseClaimFinancialsSchema,
  ClaimResponseDetailsSchema,
  ClaimResponseLineItemFinancialsSchema,
  ClaimResponseLineItemSchema,
  ClaimResponseSchema,
  ClaimResponseTransactionSchema,
  ClaimSchema,
  ContactSchema,
  CoordinatesSchema,
  CreateClaimErrorResponseSchema,
  CreateClaimRequestSchema,
  CreateClaimResponseSchema,
  CreateClaimReversalErrorResponseSchema,
  CreateClaimReversalRequestSchema,
  CreateClaimReversalResponseSchema,
  CreateClaimReversalSuccessResponseSchema,
  CreateClaimSuccessResponseSchema,
  DemographicsSchema,
  DetailsSchema,
  DiagnosisCodeSchema,
  FamilyCheckClient,
  FamilyCheckIdNumberRequestSchema,
  FamilyCheckMembershipNumberRequestSchema,
  FamilyCheckRequestSchema,
  FamilyCheckResponseSchema,
  FamilyRecordSchema,
  GenderSchema2 as GenderSchema,
  GeneralSchema,
  GeneralUpdateSchema,
  HealthbridgeClient,
  example_exports as HealthbridgeExample,
  ISODateSchema,
  ISODateTimeSchema,
  IdentifierCode,
  LineItemFinancialsSchema,
  LineItemSchema,
  MedicalAidMembershipSchema,
  MedicalAidPersonSchema,
  MedicalAidPersonSchemaOmitContact,
  MedicalAidSchema,
  MedicalAidValidationResponseSchema,
  MemberSchema,
  MoneyCentsSchema,
  PatientDetailSchema,
  PatientSchema,
  PhysicalAddressSchema,
  PlaceServiceCode,
  PostalAddressSchema,
  ProvidersSchema,
  ResponseDetailsSchema,
  ResponseSchema,
  SchemeSchema,
  StatusCode,
  StatusCodeSchema,
  SubmitClaimRequestSchema,
  SubmitClaimResultSchema,
  SubmitClaimReversalRequestSchema,
  SubmitClaimReversalResultSchema,
  TransactionSchema,
  TreatingProviderSchema,
  mapHealthbridgePatientToPerson
};
//# sourceMappingURL=index.js.map